import React, { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { SECTION } from '../../../../../shared/navigation/sections'
import { notFoundText } from '../../../../../shared/page/record-view'
import { useRoleSysProvider } from '../../../../../role/hook/role-sys'
import { useLoad } from '../../../../../shared/hooks/load'
import { IconSignInMethod } from '../../../../../shared/icon/icon'
import { WorkspaceShell, WorkspaceSection } from '../../../../../shared/sso-workspace/workspace-shell'
import { scrollToSection } from '../../../../../shared/sso-workspace/section-nav'
import { ProviderLogo } from '../../../../../shared/sso-workspace/provider-logo'
import { DockedSaveBar } from '../../../../../shared/sso-workspace/save-bar'
import { LeaveGuard } from '../../../../../shared/sso-workspace/leave-guard'
import { Notice } from '../../../../../shared/sso-workspace/fleet-page'
import { serverMessage } from '../../../../../shared/sso-workspace/format'
import { ErrorReference } from '../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../shared/error/use-error-line'
import { checkSigninMethod, getSigninMethod, setUpSigninMethod, updateSigninMethod } from '../signin-method-api'
import {
  CODE_CHECK_FAILED, CODE_MIGRATION_PENDING, CODE_UNKNOWN, MAX_LABEL, MAX_NOTICE, MAX_STAFF_NOTE, NOT_SET_UP_VIEW,
  buttonText, charactersLeft, codeOf, exactTime, lastFailureText, lastSignInText, signInCountText, stateView
} from '../signin-method-words'
import { bodyOf, changesOf, problemsOf, toDraft } from '../method-draft'
import { MethodState } from './method-state'
import { MethodConfiguration } from './method-configuration'
import { MethodHealth } from './method-health'

export const LIST_PATH = '/admin/signin-method'

const SECTIONS_SET_UP = [
  { id: 'state', label: 'State' },
  { id: 'button', label: 'Button' },
  { id: 'configuration', label: 'Configuration' },
  { id: 'health', label: 'Health' },
  { id: 'note', label: 'Staff note' }
]
const SECTIONS_NOT_SET_UP = [
  { id: 'state', label: 'State' },
  { id: 'configuration', label: 'Configuration' },
  { id: 'health', label: 'Health' }
]

// One sign-in method's screen (/admin/signin-methods/:kind; design
// edxr-workspace/.superpowers/signin-methods §5), every kind:
//
//   State          On / Temporarily unavailable / Off, asking before it
//                  leaves On; Off on Microsoft warns about its gallery
//                  listing (method-state.js)
//   Button         the label (clients add "Sign in with") and the notice
//                  shown while it is temporarily unavailable
//   Configuration  the kind's own: Microsoft's shared app read only and its
//                  two options; the platform-settings kinds' set or missing
//   Health         the last sign-in, the 7-day count, the last failure with
//                  its reference, and the kind's check
//   Staff note     staff's own
//
// History is the audit trail of the method (record type signin_method, the
// kind its id), the subheader's History through RECORD_ROUTES.
//
// Edits are the screen's own (a plain draft, method-draft.js) and saved
// with the shared docked save bar; only what changed is sent. A kind with
// no method yet shows its configuration and check, and Set up.
export const SigninMethodScreen = () => {
  const { kind } = useParams()
  const roleSysProvider = useRoleSysProvider()
  const canEdit = Boolean(roleSysProvider.accessUpdate)

  // undefined while loading, null when it couldn't be read.
  const [method, setMethod] = useState(undefined)
  const [loadCode, setLoadCode] = useState(null)
  const [loadError, loadFailure, setLoadError] = useErrorLine()
  const [draft, setDraft] = useState(null)
  const [confirmEntryPoint, setConfirmEntryPoint] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, saveFailure, setSaveError] = useErrorLine()
  const [status, setStatus] = useState('')
  const [settingUp, setSettingUp] = useState(false)
  const [check, setCheck] = useState(null)
  const [checking, setChecking] = useState(false)
  const [checkError, checkFailure, setCheckError] = useErrorLine()

  const apply = next => {
    setMethod(next)
    setDraft(toDraft(next))
    setConfirmEntryPoint(false)
  }

  useLoad(async () => {
    setMethod(undefined)
    setLoadCode(null)
    setLoadError(null)
    setStatus('')
    setCheck(null)
    try {
      apply(await getSigninMethod(kind))
    } catch (err) {
      setMethod(null)
      setLoadCode(codeOf(err))
      setLoadError(serverMessage(err, "This sign-in method couldn't be loaded. Reload to try again."), err)
    }
  }, [kind])

  const base = useMemo(() => (method ? toDraft(method) : null), [method])
  const changes = useMemo(() => changesOf(draft, base), [draft, base])
  const problems = useMemo(() => problemsOf(draft, base, method && method.defaultLabel), [draft, base, method])
  const dirty = changes.length > 0
  const blocked = Object.keys(problems).length > 0
  const editable = canEdit && Boolean(method && method.setUp)

  // The name to write in the dialogs: the label being typed when it is a
  // good one, else the saved one.
  const label = (draft && !problems.label && draft.label.trim()) || (method && (method.label || method.defaultLabel)) || kind

  const edit = patch => {
    setStatus('')
    setDraft(d => ({ ...d, ...patch }))
  }

  const chooseState = (state, { confirmEntryPoint: confirmed }) => {
    edit({ state })
    setConfirmEntryPoint(Boolean(confirmed))
  }

  const save = async () => {
    if (!dirty || blocked) return
    setSaving(true)
    setSaveError(null)
    try {
      apply(await updateSigninMethod(kind, bodyOf(draft, base, { confirmEntryPoint })))
      setStatus('Saved')
    } catch (err) {
      // A switch-on refused by its check answers with the check: show it.
      if (codeOf(err) === CODE_CHECK_FAILED && err.data && err.data.check) setCheck(err.data.check)
      setSaveError(serverMessage(err, "The changes couldn't be saved."), err)
    } finally {
      setSaving(false)
    }
  }

  const discard = () => {
    setDraft(base)
    setConfirmEntryPoint(false)
    setSaveError(null)
  }

  const setUp = async () => {
    setSettingUp(true)
    setSaveError(null)
    setStatus('')
    try {
      apply(await setUpSigninMethod(kind))
      setStatus(`${method.label || method.defaultLabel} is set up and off. Switch it on when you're ready.`)
    } catch (err) {
      setSaveError(serverMessage(err, `${label} couldn't be set up.`), err)
    } finally {
      setSettingUp(false)
    }
  }

  const runCheck = async () => {
    setChecking(true)
    setCheckError(null)
    try {
      setCheck(await checkSigninMethod(kind))
    } catch (err) {
      setCheckError(serverMessage(err, "The check couldn't run."), err)
    } finally {
      setChecking(false)
    }
  }

  // The method's name; nothing while it loads; a word for it when it didn't
  // load ("Sign-in method not found" for an unknown one) -- never the
  // lower-case path word that reached it (audit F19).
  const crumb = method ? (method.label || method.defaultLabel) : method === null ? (loadCode === CODE_UNKNOWN ? notFoundText('sign-in method') : 'Sign-in method') : ''

  return (
    <BreadcrumbView Icon={IconSignInMethod} text={crumb} parent={SECTION.signInMethods}>
      <div className="sw-pane p-3" data-testid="signin-method-pane">
        {method === undefined && <p className="sw-hint" role="status">Loading…</p>}
        {method === null && <LoadFailed kind={kind} code={loadCode} error={loadError} failure={loadFailure} />}
        {method && draft &&
          <WorkspaceShell
            title={method.label || method.defaultLabel}
            subtitle={<Subtitle method={method} />}
            logo={<ProviderLogo iconSVG={method.iconSVG} iconSVGDark={method.iconSVGDark} />}
            status={stateView(method)}
            headerActions={
              <React.Fragment>
                <span role="status" className="small text-success align-self-center">{status === 'Saved' ? 'Saved' : ''}</span>
                {!method.setUp && canEdit &&
                  <button type="button" className="btn sw-btn-primary" disabled={settingUp} onClick={setUp}>{settingUp ? 'Setting up…' : 'Set up'}</button>}
              </React.Fragment>
            }
            tiles={tilesOf(method)}
            sections={method.setUp ? SECTIONS_SET_UP : SECTIONS_NOT_SET_UP}
            onGo={scrollToSection}>
            {status && status !== 'Saved' && <p className="sw-status text-start mb-3" role="status">{status}</p>}
            {!method.setUp && saveError && <p className="text-danger small" role="alert">{saveError}</p>}
            {!method.setUp && saveError && <ErrorReference error={saveFailure} className="mb-3" />}

            <WorkspaceSection id="state" title="State" lede="Whether its button is on the sign-in pages, and whether people can sign in with it.">
              {method.setUp
                ? <MethodState method={method} label={label} saved={base.state} value={draft.state} disabled={!editable || saving} onChoose={chooseState} />
                : <p className="sw-hint m-0">{NOT_SET_UP_VIEW.sentence}</p>}
            </WorkspaceSection>

            {method.setUp &&
              <WorkspaceSection id="button" title="Button" lede="What people see on the sign-in pages.">
                <ButtonFields method={method} draft={draft} problems={problems} disabled={!editable || saving} onEdit={edit} />
              </WorkspaceSection>}

            <WorkspaceSection id="configuration" title="Configuration" lede="What it signs in with.">
              <MethodConfiguration method={method} label={label} settings={draft.settings} disabled={!editable || saving}
                onSetting={(key, value) => edit({ settings: { ...draft.settings, [key]: value } })} />
            </WorkspaceSection>

            <WorkspaceSection id="health" title="Health" lede="Whether people are signing in with it, and what went wrong last.">
              <MethodHealth method={method} label={label} check={check} checking={checking} checkError={checkError} checkFailure={checkFailure} onCheck={runCheck} />
            </WorkspaceSection>

            {method.setUp &&
              <WorkspaceSection id="note" title="Staff note" lede="Only staff see this. Every change to this method is in its History.">
                <TextField id="staffNote" label="Note" rows={4} max={MAX_STAFF_NOTE} value={draft.staffNote} problem={problems.staffNote}
                  disabled={!editable || saving} onChange={staffNote => edit({ staffNote })} />
              </WorkspaceSection>}
          </WorkspaceShell>}

        {method && method.setUp && canEdit &&
          <DockedSaveBar
            on={dirty}
            count={changes.length}
            error={saveError || (dirty && blocked ? "Can't save yet: fix the highlighted fields." : null)}
            failure={saveError ? saveFailure : null}
            busy={saving}
            canSave={dirty && !blocked}
            onSave={save}
            onDiscard={discard}
          />}
        {method && <LeaveGuard when={dirty && !saving} message={`Leave ${method.label || method.defaultLabel} without saving your changes?`} />}
      </div>
    </BreadcrumbView>
  )
}

const Subtitle = ({ method }) =>
  <React.Fragment>
    {`Shown as "${buttonText(method.label || method.defaultLabel)}"`}
    {method.identifier && <> · <code>{method.identifier}</code></>}
  </React.Fragment>

// The strip under the header: its state, its last sign-in and last failure.
const tilesOf = method => {
  const state = stateView(method)
  const tiles = [{ key: 'state', label: 'state', value: state.label, tone: state.tone, pill: true, section: 'state' }]
  if (method.setUp && method.health) {
    const now = Date.now()
    const failedAt = method.health.lastFailureAt
    tiles.push({ key: 'sign-in', label: 'last sign-in', value: lastSignInText(method.health, now), detail: signInCountText(method.health), section: 'health' })
    tiles.push({ key: 'failure', label: 'last failure', value: lastFailureText(method.health, now), detail: failedAt ? exactTime(failedAt) : undefined, tone: failedAt ? 'bad' : null, section: 'health' })
  }
  return tiles
}

// The label (clients add "Sign in with") and the notice shown while it is
// temporarily unavailable.
const ButtonFields = ({ method, draft, problems, disabled, onEdit }) =>
  <div className="row g-3">
    <div className="col-md-6">
      <div className="sw-field">
        <label className="sw-label" htmlFor="method-label">Label</label>
        <input id="method-label" className={`form-control ${problems.label ? 'is-invalid' : ''}`.trim()} value={draft.label} disabled={disabled}
          aria-describedby="method-label-hint" aria-invalid={Boolean(problems.label)} onChange={e => onEdit({ label: e.target.value })} />
        <div className="sw-hint" id="method-label-hint">
          {`Every sign-in page adds "Sign in with", so type only the name, such as "${method.defaultLabel}". At most ${MAX_LABEL} characters.`}
        </div>
        {problems.label
          ? <div className="text-danger small" role="alert">{problems.label}</div>
          : draft.label.trim() && <div className="sw-hint" data-testid="method-label-preview">{`People see "${buttonText(draft.label.trim())}".`}</div>}
      </div>
    </div>
    <div className="col-12">
      <TextField id="notice" label="Notice while temporarily unavailable" rows={2} max={MAX_NOTICE} value={draft.notice} problem={problems.notice}
        disabled={disabled} onChange={notice => onEdit({ notice })}
        hint="Shown under the greyed-out button, and on the page that explains a refused sign-in. Say what's wrong and what to do instead."
        warn={draft.state === 'unavailable' && !draft.notice.trim() ? "It's temporarily unavailable with no notice: people see only a greyed-out button." : null} />
    </div>
  </div>

const TextField = ({ id, label, rows, max, value, problem, hint, warn, disabled, onChange }) => {
  const left = charactersLeft(value, max)
  return (
    <div className="sw-field">
      <label className="sw-label" htmlFor={`method-${id}`}>{label}</label>
      <textarea id={`method-${id}`} className={`form-control ${problem ? 'is-invalid' : ''}`.trim()} rows={rows} value={value} disabled={disabled}
        aria-describedby={`method-${id}-count`} aria-invalid={Boolean(problem)} onChange={e => onChange(e.target.value)} />
      {hint && <div className="sw-hint">{hint}</div>}
      <div className={left < 0 ? 'text-danger small' : 'sw-hint'} id={`method-${id}-count`}>
        {left < 0 ? `${-left} characters over the limit of ${max}.` : `${left} characters left.`}
      </div>
      {problem && <div className="text-danger small" role="alert">{problem}</div>}
      {warn && !problem && <div className="sw-hint sw-warn" role="note">{warn}</div>}
    </div>
  )
}

// The method couldn't be read: the migration hasn't run here, the kind is
// unknown, or a real failure (with its reference).
const LoadFailed = ({ kind, code, error, failure }) => {
  const back = <Link to={LIST_PATH}>Back to Sign-in methods</Link>
  if (code === CODE_MIGRATION_PENDING) {
    return (
      <Notice tone="warn" role="status">
        <p className="m-0">{error}</p>
        <p className="m-0 mt-2">{back}</p>
      </Notice>
    )
  }
  if (code === CODE_UNKNOWN) {
    return (
      <Notice tone="warn" role="status">
        <p className="m-0">{`There's no sign-in method called "${kind}".`}</p>
        <p className="m-0 mt-2">{back}</p>
      </Notice>
    )
  }
  return (
    <div>
      <p className="text-danger small" role="alert">{error}</p>
      <ErrorReference error={failure} className="mb-3" />
      <p className="m-0">{back}</p>
    </div>
  )
}
